import { useState, useEffect, useContext } from "react";
import { toast } from "react-toastify";
import { getCurrentDate } from "../../../configs/helpers";
import { fetchedWarehousesBySubsidiary } from "../../../apis/apis.logistics/apisProduct";
import { fetchedStockInTwoWarehouses } from "../../../apis/apis.buys/apisPurchase";
import {
    sendSupplyingData,
    fetchedVehiclesBySubsidiary,
    downloadSupplyingReportExcel,
} from "../../../apis/apis.dispatches/apisDispatch";
import { ContextAuth } from "../../../contexts/ContextAuth";

const initialSupplyingState = {
    warehouseID: 0, vehicleID: 0, vehicleTwoID: 0,
    warehouseName: "", vehicleLicensePlate: "", vehicleTwoLicensePlate: "",
    readjustmentMode: "E", readjustmentMotive: "",
    supplyingDate: getCurrentDate("-"), userID: 0,
    details: [], comparatives: [],
};

const inputCls = "w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors";
const labelCls = "block text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wide mb-1";
const thCls    = "px-3 py-2 text-center text-xs font-semibold uppercase tracking-wide whitespace-nowrap border-b border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-300";
const tdCls    = "px-3 py-2 text-xs border-b border-gray-100 dark:border-gray-700 align-middle";

function warehouseLabel(item) {
    return item.gang.gangID === 0
        ? item.warehouseName
        : `${item.warehouseName} | ${item.gang.gangName}`;
}

export function SupplyingPage() {
    const [supplyingState, setSupplyingState] = useState(initialSupplyingState);
    const [vehicles, setVehicles]             = useState([]);
    const [warehouses, setWarehouses]         = useState([]);
    const [filterValue, setFilterValue]       = useState("");
    const [searchApiData, setSearchApiData]   = useState([]);
    const [isSaving, setIsSaving]             = useState(false);
    const [showReportModal, setShowReportModal] = useState(false);
    const [isDownloading, setIsDownloading]   = useState(false);
    const [reportData, setReportData]         = useState({
        warehouseOriginID: 0, warehouseDestinationID: 0,
        supplyingDate: getCurrentDate("-"),
    });
    const { auth } = useContext(ContextAuth);

    const getWarehouses = async (id) => {
        try { setWarehouses(await fetchedWarehousesBySubsidiary({ subsidiaryID: id })); }
        catch (e) { throw e; }
    };
    const getVehicles = async (id) => {
        try { setVehicles(await fetchedVehiclesBySubsidiary({ subsidiaryID: id })); }
        catch (e) { throw e; }
    };
    const getComparatives = async (outputs, inputs) => {
        try {
            const response = await fetchedStockInTwoWarehouses({ warehouseID: outputs, vehicleID: inputs });
            const values = Object.values(response);
            setSupplyingState((s) => ({ ...s, comparatives: values }));
            setSearchApiData(values);
        } catch (e) { throw e; }
    };

    useEffect(() => {
        getWarehouses(auth.data.subsidiary.subsidiary_id);
        getVehicles(auth.data.subsidiary.subsidiary_id);
    }, []);

    useEffect(() => {
        if (Number(supplyingState.warehouseID) > 0 && Number(supplyingState.vehicleID) > 0)
            getComparatives(supplyingState.warehouseID, supplyingState.vehicleID);
    }, [supplyingState.warehouseID, supplyingState.vehicleID]);

    const handleFilter = (e) => {
        const val = e.target.value;
        setFilterValue(val);
        if (val === "") {
            setSupplyingState((s) => ({ ...s, comparatives: searchApiData }));
        } else {
            const filtered = searchApiData.filter((v) =>
                v.productSku.toLowerCase().includes(val.toLowerCase()) ||
                v.productSaleName.toLowerCase().includes(val.toLowerCase()) ||
                v.productPurchaseName.toLowerCase().includes(val.toLowerCase())
            );
            if (filtered.length > 0)
                setSupplyingState((s) => ({ ...s, comparatives: filtered }));
        }
    };

    const handleClickBtnSave = async () => {
        if (!supplyingState.comparatives.length) { toast.error("Verifique los items a registrar"); return; }
        if (Number(supplyingState.vehicleID) <= 0) { toast.error("Verifique el vehículo"); return; }
        setIsSaving(true);
        try {
            const r = await sendSupplyingData(supplyingState);
            if (r.status === 201) toast.success("Operación registrada con éxito!");
            else toast.error("Ocurrió algún error");
            setSupplyingState(initialSupplyingState);
        } catch { toast.error("Error al registrar el abastecimiento"); }
        finally { setIsSaving(false); }
    };

    const handleDownloadReport = async () => {
        if (Number(reportData.warehouseOriginID) === 0)      { toast.error("Seleccione el almacén origen"); return; }
        if (Number(reportData.warehouseDestinationID) === 0) { toast.error("Seleccione el almacén destino"); return; }
        if (!reportData.supplyingDate)                       { toast.error("Seleccione una fecha"); return; }
        setIsDownloading(true);
        try {
            await downloadSupplyingReportExcel({
                warehouseOriginID:      Number(reportData.warehouseOriginID),
                warehouseDestinationID: Number(reportData.warehouseDestinationID),
                supplyingDate:          reportData.supplyingDate,
            });
            toast.success("Reporte descargado exitosamente");
            setShowReportModal(false);
        } catch { toast.error("Error al descargar el reporte"); }
        finally { setIsDownloading(false); }
    };

    const handleOpenReportModal = () => {
        setReportData({ warehouseOriginID: 0, warehouseDestinationID: 0, supplyingDate: getCurrentDate("-") });
        setShowReportModal(true);
    };

    // Detect if any row has negative stock (disable save)
    const hasNegativeStock = supplyingState.comparatives.some((item) => item.stockA < 0);
    const canSave = !isSaving && !hasNegativeStock && supplyingState.comparatives.length > 0 && Number(supplyingState.vehicleID) > 0;

    return (
        <>
            {/* Page header */}
            <section className="mb-6">
                <div className="max-w-full px-4">
                    <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <h1 className="text-2xl font-bold text-gray-900 dark:text-white tracking-tight">
                            Abastecimiento
                        </h1>
                        <nav aria-label="breadcrumb">
                            <ol className="flex items-center gap-2 text-sm">
                                <li className="text-blue-600 dark:text-blue-400">Distribuciones</li>
                                <li className="text-gray-400 dark:text-gray-500 select-none">/</li>
                                <li className="text-gray-700 dark:text-gray-300 font-medium">Abastecer</li>
                            </ol>
                        </nav>
                    </div>
                </div>
            </section>

            <section>
                <div className="max-w-full px-4 space-y-4">

                    {/* Controls card */}
                    <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm">
                        <div className="px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                            <h2 className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                                <i className="fas fa-boxes text-blue-500"></i>
                                Configuración de abastecimiento
                            </h2>
                        </div>
                        <div className="px-5 py-4 space-y-4">
                            {/* Row 1: selects + date + save */}
                            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
                                {/* Almacén origen */}
                                <div>
                                    <label className={`${labelCls} text-blue-600 dark:text-blue-400`}>
                                        <i className="fas fa-warehouse mr-1"></i>Almacén Origen
                                    </label>
                                    <select
                                        name="warehouseID"
                                        value={supplyingState.warehouseID}
                                        onChange={async (e) => {
                                            const found = warehouses.find((w) => w.warehouseID == e.target.value);
                                            if (found) setSupplyingState({ ...supplyingState, warehouseID: found.warehouseID, warehouseName: found.warehouseName, userID: auth.data.user_id });
                                        }}
                                        className={`${inputCls} text-blue-700 dark:text-blue-300 font-medium`}
                                    >
                                        <option value="0">Elegir...</option>
                                        {warehouses.map((item, i) => <option key={i} value={item.warehouseID}>{warehouseLabel(item)}</option>)}
                                    </select>
                                </div>
                                {/* Vehículo destino */}
                                <div>
                                    <label className={`${labelCls} text-orange-600 dark:text-orange-400`}>
                                        <i className="fas fa-truck mr-1"></i>Vehículo Destino
                                    </label>
                                    <select
                                        name="vehicleID"
                                        value={supplyingState.vehicleID}
                                        onChange={(e) => setSupplyingState({ ...supplyingState, vehicleID: e.target.value, vehicleLicensePlate: e.nativeEvent.target[e.nativeEvent.target.selectedIndex].text })}
                                        className={`${inputCls} text-orange-700 dark:text-orange-300 font-medium`}
                                    >
                                        <option value="0">Elegir...</option>
                                        {vehicles.map((item, i) => <option key={i} value={item.vehicleID}>{item.vehicleLicensePlate} | {item.gang.gangName}</option>)}
                                    </select>
                                </div>
                                {/* Fecha */}
                                <div>
                                    <label className={labelCls}><i className="fas fa-calendar mr-1"></i>Fecha</label>
                                    <input
                                        type="date"
                                        name="supplyingDate"
                                        value={supplyingState.supplyingDate}
                                        onChange={(e) => setSupplyingState({ ...supplyingState, supplyingDate: e.target.value })}
                                        className={inputCls}
                                    />
                                </div>
                                {/* Abastecer */}
                                <div>
                                    <button
                                        type="button"
                                        disabled={!canSave}
                                        onClick={handleClickBtnSave}
                                        className="w-full inline-flex items-center justify-center gap-2 rounded-lg bg-amber-500 hover:bg-amber-600 active:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm px-4 py-2.5 transition-colors focus:outline-none focus:ring-2 focus:ring-amber-400"
                                    >
                                        {isSaving
                                            ? <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin"></span>
                                            : <i className="fas fa-save"></i>}
                                        {isSaving ? "Guardando..." : "Abastecer"}
                                    </button>
                                </div>
                            </div>
                            {/* Row 2: report button */}
                            <div className="flex items-center gap-4">
                                <button
                                    type="button"
                                    onClick={handleOpenReportModal}
                                    className="inline-flex items-center gap-1.5 rounded-lg bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-medium px-4 py-2 transition-colors focus:outline-none focus:ring-2 focus:ring-cyan-500"
                                >
                                    <i className="fas fa-file-excel"></i>
                                    Generar Reporte Excel
                                </button>
                                {hasNegativeStock && (
                                    <span className="inline-flex items-center gap-1.5 text-xs text-red-600 dark:text-red-400 font-medium">
                                        <i className="fas fa-exclamation-triangle"></i>
                                        Stock insuficiente en algún producto
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Products table */}
                    {supplyingState.comparatives.length > 0 && (
                        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm overflow-hidden">
                            {/* Table header */}
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 px-5 py-4 border-b border-gray-200 dark:border-gray-700">
                                <div>
                                    <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                                        Productos a abastecer
                                    </h2>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                        {supplyingState.comparatives.length} productos
                                    </p>
                                </div>
                                <div className="w-full sm:w-72">
                                    <input
                                        type="search"
                                        placeholder="Buscar por SKU o producto..."
                                        value={filterValue}
                                        onInput={handleFilter}
                                        onChange={handleFilter}
                                        className={inputCls}
                                    />
                                </div>
                            </div>

                            <div className="overflow-x-auto max-h-[65vh] overflow-y-auto bg-white dark:bg-gray-900">
                                <table className="min-w-full text-sm border-separate border-spacing-0">
                                    <thead className="sticky top-0 z-10">
                                        <tr>
                                            <th className={thCls} style={{width:"5%"}}>Código</th>
                                            <th className={thCls} style={{width:"8%"}}>Clasificación</th>
                                            <th className={thCls} style={{width:"9%"}}>SKU</th>
                                            <th className={`${thCls} text-left`} style={{width:"20%"}}>Producto</th>
                                            <th className={thCls} style={{width:"9%"}}>P. Venta</th>
                                            <th className={`${thCls} text-blue-700 dark:text-blue-300`} style={{width:"9%"}}>
                                                Stock Actual
                                                <br/><span className="text-[10px] font-normal text-gray-400">({supplyingState.warehouseName.split(" ").pop()})</span>
                                            </th>
                                            <th className={`${thCls} text-emerald-700 dark:text-emerald-300`} style={{width:"9%"}}>
                                                Stock Inicial
                                                <br/><span className="text-[10px] font-normal text-gray-400">({supplyingState.vehicleLicensePlate.split(" ").pop()})</span>
                                            </th>
                                            <th className={`${thCls} text-orange-700 dark:text-orange-300`} style={{width:"9%"}}>
                                                Stock Total
                                                <br/><span className="text-[10px] font-normal text-gray-400">({supplyingState.vehicleLicensePlate.split(" ").pop()})</span>
                                            </th>
                                            <th className={thCls} style={{width:"22%"}}>Cantidad a Abastecer</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {supplyingState.comparatives
                                            .sort((a, b) => a.productCode > b.productCode ? 1 : -1)
                                            .map((item, index) => {
                                                const hasQty = Number(item.quantity) > 0;
                                                return (
                                                    <tr key={index} className={hasQty ? "bg-amber-50 dark:bg-amber-900/10" : "bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800/40"}>
                                                        <td className={`${tdCls} text-center font-bold text-gray-700 dark:text-gray-300`}>{item.productCode}</td>
                                                        <td className={`${tdCls} text-center`}>
                                                            <span className="inline-block rounded-full bg-cyan-100 dark:bg-cyan-900/40 text-cyan-700 dark:text-cyan-300 text-xs font-medium px-2 py-0.5">
                                                                {item.productClassificationDisplay}
                                                            </span>
                                                        </td>
                                                        <td className={`${tdCls} text-center font-mono text-gray-600 dark:text-gray-400`}>{item.productSku}</td>
                                                        <td className={`${tdCls} text-left font-medium text-gray-800 dark:text-gray-200`}>{item.productSaleName}</td>
                                                        <td className={`${tdCls} text-right font-semibold text-gray-700 dark:text-gray-300`}>
                                                            S/ {Number(item.priceSale).toFixed(2)}
                                                        </td>
                                                        <td className={`${tdCls} text-center font-bold text-lg text-blue-700 dark:text-blue-300 ${item.stockA < 0 ? "bg-red-50 dark:bg-red-900/20" : ""}`}>
                                                            {item.stockA}
                                                        </td>
                                                        <td className={`${tdCls} text-center font-bold text-lg text-emerald-700 dark:text-emerald-300`}>{item.fixedStockB}</td>
                                                        <td className={`${tdCls} text-center font-bold text-lg text-orange-700 dark:text-orange-300`}>{item.stockB}</td>
                                                        <td className={`${tdCls}`}>
                                                            {item.remainingQuantityInTariffs.map((tariffObj, ti) => (
                                                                <div key={ti} className="flex items-center gap-1 mb-1 last:mb-0">
                                                                    <span className={`flex-shrink-0 w-10 text-center rounded text-xs font-bold py-1 ${tariffObj.unitName === "CJ" ? "bg-amber-400 text-gray-900" : "bg-gray-500 text-white"}`}>
                                                                        {tariffObj.unitName}
                                                                    </span>
                                                                    <input
                                                                        type="number"
                                                                        onWheel={(e) => e.target.blur()}
                                                                        value={tariffObj.portion}
                                                                        className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm text-center px-1 py-1 focus:outline-none focus:ring-2 focus:ring-blue-500 font-bold"
                                                                        onChange={(e) => {
                                                                            const val = e.target.value;
                                                                            if (Number(val) >= 0 && Number(val) <= Number(item.fixedStockA) / tariffObj.quantityMinimum) {
                                                                                setSupplyingState((prev) => ({
                                                                                    ...prev,
                                                                                    comparatives: prev.comparatives.map((obj) => {
                                                                                        if (obj.productID === item.productID) {
                                                                                            obj.remainingQuantityInTariffs = item.remainingQuantityInTariffs.map((tariff) => {
                                                                                                if (Number(tariff.productTariffID) === Number(tariffObj.productTariffID)) {
                                                                                                    item.quantity = item.quantity - Number(tariff.portion) * tariff.quantityMinimum;
                                                                                                    if (Number(val) > 0) item.quantity = item.quantity + Number(val) * tariff.quantityMinimum;
                                                                                                    tariff.portion = val;
                                                                                                }
                                                                                                return tariff;
                                                                                            });
                                                                                            item.stockA = item.fixedStockA - item.quantity;
                                                                                            item.stockB = item.fixedStockB + item.quantity;
                                                                                        }
                                                                                        return obj;
                                                                                    }),
                                                                                }));
                                                                            }
                                                                        }}
                                                                    />
                                                                </div>
                                                            ))}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}
                </div>
            </section>

            {/* Report modal */}
            {showReportModal && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center p-4"
                    aria-modal="true"
                    role="dialog"
                >
                    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowReportModal(false)} />
                    <div className="relative z-10 w-full max-w-lg flex flex-col rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-2xl overflow-hidden">

                        {/* Header */}
                        <div className="flex items-center justify-between px-6 py-4 bg-cyan-600 dark:bg-cyan-700 flex-shrink-0">
                            <h5 className="text-base font-semibold text-white flex items-center gap-2">
                                <i className="fas fa-file-excel"></i>
                                Generar Reporte de Abastecimiento
                            </h5>
                            <button
                                type="button"
                                onClick={() => setShowReportModal(false)}
                                className="text-white/80 hover:text-white transition-colors p-1 rounded"
                                aria-label="Cerrar"
                            >
                                <i className="fas fa-times text-lg"></i>
                            </button>
                        </div>

                        {/* Body */}
                        <div className="px-6 py-5 space-y-4 bg-white dark:bg-gray-800">

                            {/* Almacén origen */}
                            <div>
                                <label className={`${labelCls} text-blue-600 dark:text-blue-400`}>
                                    <i className="fas fa-warehouse mr-1"></i>Almacén Origen
                                </label>
                                <select
                                    value={reportData.warehouseOriginID}
                                    onChange={(e) => {
                                        const found = warehouses.find((w) => w.warehouseID == e.target.value);
                                        if (found) setReportData({ ...reportData, warehouseOriginID: found.warehouseID });
                                    }}
                                    className={`${inputCls} text-blue-700 dark:text-blue-300 font-medium`}
                                >
                                    <option value="0">Elegir...</option>
                                    {warehouses.map((item, i) => (
                                        <option key={i} value={item.warehouseID}>{warehouseLabel(item)}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Almacén destino */}
                            <div>
                                <label className={`${labelCls} text-orange-600 dark:text-orange-400`}>
                                    <i className="fas fa-warehouse mr-1"></i>Almacén Destino
                                </label>
                                <select
                                    value={reportData.warehouseDestinationID}
                                    onChange={(e) => {
                                        const found = warehouses.find((w) => w.warehouseID == e.target.value);
                                        if (found) setReportData({ ...reportData, warehouseDestinationID: found.warehouseID });
                                    }}
                                    className={`${inputCls} text-orange-700 dark:text-orange-300 font-medium`}
                                >
                                    <option value="0">Elegir...</option>
                                    {warehouses.map((item, i) => (
                                        <option key={i} value={item.warehouseID}>{warehouseLabel(item)}</option>
                                    ))}
                                </select>
                            </div>

                            {/* Fecha */}
                            <div>
                                <label className={labelCls}><i className="fas fa-calendar mr-1"></i>Fecha</label>
                                <input
                                    type="date"
                                    value={reportData.supplyingDate}
                                    onChange={(e) => setReportData({ ...reportData, supplyingDate: e.target.value })}
                                    className={inputCls}
                                />
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex-shrink-0">
                            <button
                                type="button"
                                onClick={() => setShowReportModal(false)}
                                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 text-sm font-medium px-4 py-2 transition-colors"
                            >
                                <i className="fas fa-times"></i>
                                Cancelar
                            </button>
                            <button
                                type="button"
                                onClick={handleDownloadReport}
                                disabled={isDownloading}
                                className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 active:bg-emerald-800 disabled:opacity-50 disabled:cursor-not-allowed text-white font-semibold text-sm px-4 py-2 transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-500"
                            >
                                {isDownloading
                                    ? <span className="h-4 w-4 rounded-full border-2 border-white border-t-transparent animate-spin"></span>
                                    : <i className="fas fa-download"></i>}
                                {isDownloading ? "Descargando..." : "Descargar Reporte"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
}